import React from "react";
import FileList from "./FileList";
import { Button, IconButton, Divider, Typography } from "@mui/material";
import {
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon,
} from "@mui/icons-material";
import { deleteFile } from "../../../../services/StorageServices";
import { useState } from "react";

function FileListArea({
  singlePageFiles,
  multiPageFiles,
  selectedSinglePageFileIds,
  selectedMultiPageFileIds,
  handleSinglePageFileSelection,
  handleMultiPageFileSelection,
  selectAllSinglePage,
  handleSelectAllSinglePage,
  viewSizeSinglePage,
  viewSizeMultiPage,
  handleViewSizeChangeSinglePage,
  handleViewSizeChangeMultiPage,
  error,
  success,
  loading,
  uploadProgress,
  setSinglePageFiles,
  setMultiPageFiles,
  setError,
  setSuccess,
}) {
  // Determine disabled states based on selection in the opposite panel
  const isSinglePageDisabled = selectedMultiPageFileIds.length > 0;
  const isMultiPageDisabled = selectedSinglePageFileIds.length > 0;

  // Handler for deleting a file
  const handleDeleteFile = async (file) => {
    try {
      if (!file || !file.storagePath) {
        if (setError) setError("Invalid file or missing storage path.");
        return;
      }
      await deleteFile(file.storagePath);
      if (setSuccess) setSuccess("File deleted successfully.");
      // Remove from correct list
      if (singlePageFiles.some((f) => f.id === file.id)) {
        setSinglePageFiles &&
          setSinglePageFiles((prev) => prev.filter((f) => f.id !== file.id));
      } else if (multiPageFiles.some((f) => f.id === file.id)) {
        setMultiPageFiles &&
          setMultiPageFiles((prev) => prev.filter((f) => f.id !== file.id));
      }
    } catch (err) {
      if (setError) setError("Failed to delete file: " + err.message);
    }
  };

  return (
    <div
      style={{
        flexGrow: 1,
        height: "100%",
        display: "flex",
        flexDirection: "row",
        gap: 16,
      }}
    >
      {/* Left Panel: Single Page Documents */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid #e0e0e0",
          height: "100%",
          opacity: isSinglePageDisabled ? 0.5 : 1,
          pointerEvents: isSinglePageDisabled ? "none" : "auto",
        }}
      >
        <Typography
          variant="h6"
          sx={{ padding: 2, borderBottom: "1px solid #e0e0e0" }}
        >
          Single Page Documents
        </Typography>
        {/* Select All and View Size Controls */}
        <div
          style={{
            padding: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <div>
            <label>
              <input
                type="checkbox"
                checked={selectAllSinglePage}
                onChange={handleSelectAllSinglePage}
                style={{ marginRight: 8 }}
                disabled={isSinglePageDisabled}
              />
              Select All
            </label>
          </div>
          <div>
            <IconButton
              color={viewSizeSinglePage === "small" ? "primary" : "default"}
              onClick={() => handleViewSizeChangeSinglePage(null, "small")}
              size="small"
              sx={{ mr: 1 }}
              aria-label="small view"
              disabled={isSinglePageDisabled}
            >
              <ViewListIcon />
            </IconButton>
            <IconButton
              color={viewSizeSinglePage === "medium" ? "primary" : "default"}
              onClick={() => handleViewSizeChangeSinglePage(null, "medium")}
              size="small"
              sx={{ mr: 1 }}
              aria-label="medium view"
              disabled={isSinglePageDisabled}
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              color={viewSizeSinglePage === "large" ? "primary" : "default"}
              onClick={() => handleViewSizeChangeSinglePage(null, "large")}
              size="small"
              aria-label="large view"
              disabled={isSinglePageDisabled}
            >
              <ViewComfyIcon />
            </IconButton>
          </div>
        </div>
        {/* File List for Single Page Documents */}
        <FileList
          files={singlePageFiles}
          selectedFileIds={selectedSinglePageFileIds}
          handleFileSelection={handleSinglePageFileSelection}
          selectAll={selectAllSinglePage}
          onSelectAll={handleSelectAllSinglePage}
          viewSize={viewSizeSinglePage}
          getFilePreviewUrl={(file) => {
            if (file.downloadURL) {
              return file.downloadURL;
            } else if (
              file.fileObject &&
              (file.fileObject instanceof Blob ||
                file.fileObject instanceof File)
            ) {
              return URL.createObjectURL(file.fileObject);
            } else if (file instanceof Blob || file instanceof File) {
              return URL.createObjectURL(file);
            } else {
              return null;
            }
          }}
          error={error}
          success={success}
          loading={loading}
          uploadProgress={uploadProgress}
          disabled={isSinglePageDisabled}
          onDelete={handleDeleteFile}
        />
      </div>

      {/* Right Panel: Multi Page Documents */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          opacity: isMultiPageDisabled ? 0.5 : 1,
          pointerEvents: isMultiPageDisabled ? "none" : "auto",
        }}
      >
        <Typography
          variant="h6"
          sx={{ padding: 2, borderBottom: "1px solid #e0e0e0" }}
        >
          Multi Page Documents
        </Typography>
        {/* No Select All for right panel */}
        <div
          style={{
            padding: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <IconButton
            color={viewSizeMultiPage === "small" ? "primary" : "default"}
            onClick={() => handleViewSizeChangeMultiPage(null, "small")}
            size="small"
            sx={{ mr: 1 }}
            aria-label="small view"
            disabled={isMultiPageDisabled}
          >
            <ViewListIcon />
          </IconButton>
          <IconButton
            color={viewSizeMultiPage === "medium" ? "primary" : "default"}
            onClick={() => handleViewSizeChangeMultiPage(null, "medium")}
            size="small"
            sx={{ mr: 1 }}
            aria-label="medium view"
            disabled={isMultiPageDisabled}
          >
            <ViewModuleIcon />
          </IconButton>
          <IconButton
            color={viewSizeMultiPage === "large" ? "primary" : "default"}
            onClick={() => handleViewSizeChangeMultiPage(null, "large")}
            size="small"
            aria-label="large view"
            disabled={isMultiPageDisabled}
          >
            <ViewComfyIcon />
          </IconButton>
        </div>
        {/* File List for Multi Page Documents */}
        <FileList
          files={multiPageFiles}
          selectedFileIds={selectedMultiPageFileIds}
          handleFileSelection={handleMultiPageFileSelection}
          selectAll={false}
          onSelectAll={() => {}}
          viewSize={viewSizeMultiPage}
          getFilePreviewUrl={(file) => {
            if (
              file.fileObject &&
              (file.fileObject instanceof Blob ||
                file.fileObject instanceof File)
            ) {
              return URL.createObjectURL(file.fileObject);
            } else if (file instanceof Blob || file instanceof File) {
              return URL.createObjectURL(file);
            } else {
              return null;
            }
          }}
          error={error}
          success={success}
          loading={loading}
          uploadProgress={uploadProgress}
          disabled={isMultiPageDisabled}
          onDelete={handleDeleteFile}
        />
      </div>
    </div>
  );
}

export default FileListArea;
